import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Text, BlockStack } from '@shopify/polaris';

const GenerateStoryConfirmModal = ({
  isOpen,
  onClose,
  preview,
  setToastMessage,
  setToastActive,
  onGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedGenerationId, setGeneratedGenerationId] = useState('');

  const generateComplexGenerationId = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let suffix = '';
    for (let i = 0; i < 6; i += 1) {
      suffix += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `GEN${Date.now()}-${suffix}`;
  };

  useEffect(() => {
    if (isOpen) {
      setGeneratedGenerationId(generateComplexGenerationId());
    }
  }, [isOpen]);

  const details = useMemo(() => {
    if (!preview) {
      return {
        kidName: '',
        storyName: '',
        kidImage: '',
        generationId: '',
        generationCover: '',
        slug: '',
      };
    }
    const storyName = preview.story && preview.story.title ? preview.story.title : (preview.story_slug || '');
    const generationCover = (preview.generated_scenes && preview.generated_scenes[0] && preview.generated_scenes[0].processedImageUrl) || '';
    const slug = preview.story_slug || (preview.story && preview.story.slug) || '';
    return {
      kidName: preview.kid_name || '',
      storyName,
      kidImage: preview.kid_image || '',
      generationId: preview.generation_id || '',
      generationCover,
      slug,
    };
  }, [preview]);

  const handleGenerate = async () => {
    if (!details.slug || !details.kidName || !details.kidImage || !generatedGenerationId) {
      setToastMessage && setToastMessage('Missing required data to generate story');
      setToastActive && setToastActive(true);
      return;
    }
    setIsGenerating(true);
    try {
      const { generationsService } = await import('../../services/generationsService');
      await generationsService.createGeneration({
        slug: details.slug,
        kid_name: details.kidName,
        kid_image: details.kidImage,
        generation_id: generatedGenerationId,
        generation_provider_override: preview?.story?.generation_provider_override || preview?.generation_provider_override || '',
        generation_model_override: preview?.story?.generation_model_override || preview?.generation_model_override || '',
      });
      setToastMessage && setToastMessage('Story generation started');
      setToastActive && setToastActive(true);
      onClose && onClose();
      onGenerated && onGenerated();
    } catch (error) {
      console.error('Error generating story:', error);
      setToastMessage && setToastMessage(error.message || 'Failed to start story generation');
      setToastActive && setToastActive(true);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{ padding: '16px' }}>{'Confirm Generate Story'}</div>}
      primaryAction={{
        content: 'Generate',
        onAction: handleGenerate,
        loading: isGenerating,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
      footer={<div style={{ padding: '16px' }}></div>}
    >
      <BlockStack gap="400">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <tbody>
            <tr>
              <td style={{ padding: '10px 8px', width: '35%', color: '#6d7175', fontSize: 13, textAlign: 'left', background: '#fafbfc', borderBottom: '1px solid #e3e3e3', fontWeight: 600 }}>Kid Name</td>
              <td style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>
                <Text variant="bodyMd">{details.kidName || '-'}</Text>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 8px', width: '35%', color: '#6d7175', fontSize: 13, textAlign: 'left', background: '#fafbfc', borderBottom: '1px solid #e3e3e3', fontWeight: 600 }}>Story Name</td>
              <td style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>
                <Text variant="bodyMd">{details.storyName || '-'}</Text>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 8px', width: '35%', color: '#6d7175', fontSize: 13, textAlign: 'left', background: '#fafbfc', borderBottom: '1px solid #e3e3e3', fontWeight: 600 }}>Kid Image</td>
              <td style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>
                {details.kidImage ? (
                  <div style={{ width: 64, height: 64, borderRadius: 8, overflow: 'hidden', background: '#f6f6f7' }}>
                    <img src={details.kidImage} alt={details.kidName || 'Kid Image'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ) : (
                  <Text variant="bodyMd">-</Text>
                )}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 8px', width: '35%', color: '#6d7175', fontSize: 13, textAlign: 'left', background: '#fafbfc', borderBottom: '1px solid #e3e3e3', fontWeight: 600 }}>Previewe ID</td>
              <td style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>
                <Text variant="bodyMd">{details.generationId || '-'}</Text>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 8px', width: '35%', color: '#6d7175', fontSize: 13, textAlign: 'left', background: '#fafbfc', borderBottom: '1px solid #e3e3e3', fontWeight: 600 }}>Generation Cover</td>
              <td style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>
                {details.generationCover ? (
                  <div style={{ width: 96, height: 96, borderRadius: 8, overflow: 'hidden', background: '#f6f6f7' }}>
                    <img src={details.generationCover} alt="Generation Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ) : (
                  <Text variant="bodyMd">-</Text>
                )}
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ textAlign: 'left' }}>
          <Text variant="bodySm" tone="subdued">This will start generating the selected story for this kid.</Text>
        </div>
      </BlockStack>
    </Modal>
  );
};

export default GenerateStoryConfirmModal;
